import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUserPlus,
  FaMicrochip,
  FaCopy,
  FaCheckCircle,
  FaArrowLeft,
  FaRandom,
  FaSave,
  FaSearch,
  FaUserCheck,
  FaTimes,
  FaCalendarAlt,
} from "react-icons/fa";

import { request } from "../../services/api";
import { CircuitBackgroundLayer } from "../../components/CircuitBackground/AnimatedCircuitChipBackground";
import styles from "../Monitor/Monitor.module.css";

const AdminCreateUserDevice = () => {
  const navigate = useNavigate();
  const userDropdownRef = useRef(null);
  const isMobile = useIsMobile();

  const [mode, setMode] = useState("new_user"); // new_user | existing_user

  const [form, setForm] = useState({
    username: "",
    password: "",
    name: "",
    family: "",
    phone_num1: "",
    phone_num2: "",
    device_serial: "",
    device_name: "",
    service_number: "",
    start_date: "",
    due_date: "",
  });

  const [userSearch, setUserSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetResultAndError = () => {
    setResult(null);
    setError("");
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setResult(null);
    setError("");
    setSelectedUser(null);
    setUserSearch("");
    setUsers([]);
    setIsUserDropdownOpen(false);

    setForm({
      username: "",
      password: "",
      name: "",
      family: "",
      phone_num1: "",
      phone_num2: "",
      device_serial: "",
      device_name: "",
      service_number: "",
      start_date: "",
      due_date: "",
    });
  };

  const generateUsernameFromPhone = () => {
    const phone = form.phone_num1.trim();

    if (!phone) {
      setError("برای ساخت نام کاربری، ابتدا شماره موبایل اصلی را وارد کنید.");
      return;
    }

    const cleanPhone = phone.replace(/\D/g, "");
    updateField("username", cleanPhone);
    setError("");
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#$";
    let pass = "";

    for (let i = 0; i < 10; i++) {
      pass += chars[Math.floor(Math.random() * chars.length)];
    }

    updateField("password", pass);
    setError("");
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("کپی شد");
    } catch {
      alert("امکان کپی وجود ندارد");
    }
  };

  const validateNewUserForm = () => {
    if (!form.username.trim()) return "نام کاربری الزامی است.";
    if (!form.password.trim()) return "رمز عبور الزامی است.";
    if (form.password.trim().length < 6)
      return "رمز عبور باید حداقل ۶ کاراکتر باشد.";
    if (!form.phone_num1.trim()) return "شماره موبایل اصلی الزامی است.";
    if (!form.device_serial.trim()) return "سریال دستگاه الزامی است.";
    if (!form.service_number.trim()) return "شماره سرویس الزامی است.";

    return "";
  };

  const validateExistingUserForm = () => {
    if (!selectedUser) return "ابتدا یک کاربر موجود را انتخاب کنید.";
    if (!form.device_serial.trim()) return "سریال دستگاه الزامی است.";
    if (!form.service_number.trim()) return "شماره سرویس الزامی است.";

    return "";
  };

  const searchUsers = async (searchText = userSearch) => {
    setUsersLoading(true);
    setError("");

    try {
      const q = searchText.trim();

      const json = await request(
        `/admin_get_users.php?search=${encodeURIComponent(q)}&limit=50`,
      );

      if (!json || json.status !== "success") {
        throw new Error(json?.message || "خطا در دریافت کاربران");
      }

      setUsers(json.users || []);
    } catch (err) {
      console.error("Admin get users error:", err);
      setError(err.message || "خطا در دریافت کاربران");
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (mode !== "existing_user") return;
    if (!isUserDropdownOpen) return;

    const timer = setTimeout(() => {
      searchUsers(userSearch);
    }, 350);

    return () => clearTimeout(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch, mode, isUserDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleUserInputFocus = () => {
    setIsUserDropdownOpen(true);

    if (users.length === 0) {
      searchUsers(userSearch);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setUserSearch(
      `${user.name || ""} ${user.family || ""} - ${user.phone_num1 || user.username || ""}`.trim(),
    );
    setIsUserDropdownOpen(false);
    setResult(null);
    setError("");
  };

  const clearSelectedUser = () => {
    setSelectedUser(null);
    setUserSearch("");
    setUsers([]);
    setIsUserDropdownOpen(true);
    setResult(null);
    setError("");

    setTimeout(() => {
      searchUsers("");
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError("");
    setResult(null);

    try {
      if (mode === "new_user") {
        const validationError = validateNewUserForm();

        if (validationError) {
          setError(validationError);
          setLoading(false);
          return;
        }

        const payload = {
          username: form.username.trim(),
          password: form.password.trim(),
          name: form.name.trim(),
          family: form.family.trim(),
          phone_num1: form.phone_num1.trim(),
          phone_num2: form.phone_num2.trim(),
          device_serial: form.device_serial.trim(),
          device_name: form.device_name.trim(),
          service_number: form.service_number.trim(),
          start_date: form.start_date || "",
          due_date: form.due_date || "",
        };

        const json = await request(
          "/admin_create_user_device.php",
          "POST",
          payload,
        );

        if (!json || json.status !== "success") {
          throw new Error(json?.message || "خطا در ثبت کاربر و دستگاه");
        }

        setResult({
          ...json,
          mode: "new_user",
        });
      }

      if (mode === "existing_user") {
        const validationError = validateExistingUserForm();

        if (validationError) {
          setError(validationError);
          setLoading(false);
          return;
        }

        const payload = {
          owner_id: selectedUser.id,
          device_serial: form.device_serial.trim(),
          device_name: form.device_name.trim(),
          service_number: form.service_number.trim(),
          start_date: form.start_date || "",
          due_date: form.due_date || "",
        };

        const json = await request("/admin_assign_device.php", "POST", payload);

        if (!json || json.status !== "success") {
          throw new Error(json?.message || "خطا در افزودن دستگاه به کاربر");
        }

        setResult({
          ...json,
          mode: "existing_user",
        });

        searchUsers(userSearch);
      }
    } catch (err) {
      console.error("Admin submit error:", err);
      setError(err.message || "خطای نامشخص");
    } finally {
      setLoading(false);
    }
  };

  const handleNewForm = () => {
    setForm({
      username: "",
      password: "",
      name: "",
      family: "",
      phone_num1: "",
      phone_num2: "",
      device_serial: "",
      device_name: "",
      service_number: "",
      start_date: "",
      due_date: "",
    });

    setSelectedUser(null);
    setUserSearch("");
    setUsers([]);
    setIsUserDropdownOpen(false);
    setResult(null);
    setError("");
  };

  const buildUserMessage = () => {
    if (!result) return "";

    if (result.mode === "new_user") {
      return [
        "اطلاعات ورود به پنل هوشمند گلخانه:",
        `نام کاربری: ${result.user.username}`,
        `رمز عبور: ${result.user.plain_password}`,
        `سریال دستگاه: ${result.device.device_serial}`,
        `شماره سرویس: ${result.device.service_number || "-"}`,
        `نام دستگاه: ${result.device.device_name || "-"}`,
        "آدرس پنل: https://sfarm.tabangohar.com",
      ].join("\n");
    }

    return [
      "دستگاه جدید به حساب شما اضافه شد:",
      `نام کاربر: ${result.user.name || ""} ${result.user.family || ""}`,
      `نام کاربری: ${result.user.username}`,
      `سریال دستگاه: ${result.device.device_serial}`,
      `شماره سرویس: ${result.device.service_number || "-"}`,
      `نام دستگاه: ${result.device.device_name || "-"}`,
      "آدرس پنل: https://sfarm.tabangohar.com",
    ].join("\n");
  };

  return (
    <div className={styles.container}>
      <CircuitBackgroundLayer />
      <div className={styles.overlay}></div>

      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.deviceTitle}>
            <span style={{ fontSize: "2rem" }}>👤</span>
            <div>
              <div style={{ fontWeight: "bold" }}>تعریف کاربر و دستگاه</div>
              <span style={{ fontSize: "12px", color: "#ccc" }}>
                ایجاد مشتری جدید یا افزودن دستگاه به کاربر موجود
              </span>
            </div>
          </div>

          <button
            onClick={() => navigate("/admin")}
            className={styles.backBtn}
            type="button"
          >
            <FaArrowLeft /> بازگشت به مدیریت
          </button>
        </div>

        <div style={getModeSwitchStyle(isMobile)}>
          <button
            type="button"
            onClick={() => switchMode("new_user")}
            style={getModeButtonStyle(isMobile, mode === "new_user")}
          >
            <FaUserPlus />
            کاربر جدید + دستگاه اول
          </button>

          <button
            type="button"
            onClick={() => switchMode("existing_user")}
            style={getModeButtonStyle(isMobile, mode === "existing_user")}
          >
            <FaUserCheck />
            افزودن دستگاه به کاربر موجود
          </button>
        </div>

        <div style={getPageGridStyle(isMobile)}>
          <form onSubmit={handleSubmit} style={getFormCardStyle(isMobile)}>
            {mode === "new_user" && (
              <>
                <div style={sectionTitleStyle}>
                  <FaUserPlus color="#2ecc71" />
                  <span>اطلاعات کاربر جدید</span>
                </div>

                <div style={getGridStyle(isMobile)}>
                  <div>
                    <label style={labelStyle}>شماره موبایل اصلی *</label>
                    <input
                      style={inputStyle}
                      value={form.phone_num1}
                      onChange={(e) => {
                        updateField("phone_num1", e.target.value);
                        resetResultAndError();
                      }}
                      placeholder="مثلا 09123456789"
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>نام کاربری *</label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input
                        style={inputStyle}
                        value={form.username}
                        onChange={(e) => {
                          updateField("username", e.target.value);
                          resetResultAndError();
                        }}
                        placeholder="نام کاربری"
                      />
                      <button
                        type="button"
                        onClick={generateUsernameFromPhone}
                        style={smallButtonStyle}
                        title="ساخت از شماره موبایل"
                      >
                        <FaRandom />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>رمز عبور اولیه *</label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input
                        style={inputStyle}
                        value={form.password}
                        onChange={(e) => {
                          updateField("password", e.target.value);
                          resetResultAndError();
                        }}
                        placeholder="رمز اولیه"
                      />
                      <button
                        type="button"
                        onClick={generatePassword}
                        style={smallButtonStyle}
                        title="تولید رمز"
                      >
                        تولید
                      </button>
                    </div>
                  </div>

                  <Input
                    label="شماره موبایل دوم"
                    value={form.phone_num2}
                    onChange={(v) => {
                      updateField("phone_num2", v);
                      resetResultAndError();
                    }}
                    placeholder="اختیاری"
                  />

                  <Input
                    label="نام"
                    value={form.name}
                    onChange={(v) => {
                      updateField("name", v);
                      resetResultAndError();
                    }}
                    placeholder="نام مشتری"
                  />

                  <Input
                    label="نام خانوادگی"
                    value={form.family}
                    onChange={(v) => {
                      updateField("family", v);
                      resetResultAndError();
                    }}
                    placeholder="نام خانوادگی مشتری"
                  />
                </div>
              </>
            )}

            {mode === "existing_user" && (
              <>
                <div style={sectionTitleStyle}>
                  <FaUserCheck color="#2ecc71" />
                  <span>انتخاب کاربر موجود</span>
                </div>

                <div style={userDropdownWrapperStyle} ref={userDropdownRef}>
                  <label style={labelStyle}>جستجو و انتخاب کاربر</label>

                  <div style={userSearchInputWrapperStyle}>
                    <input
                      style={{
                        ...inputStyle,
                        paddingLeft: selectedUser ? "38px" : "12px",
                      }}
                      value={userSearch}
                      onFocus={handleUserInputFocus}
                      onClick={handleUserInputFocus}
                      onChange={(e) => {
                        setUserSearch(e.target.value);
                        setSelectedUser(null);
                        setResult(null);
                        setError("");
                        setIsUserDropdownOpen(true);
                      }}
                      placeholder="نام، نام خانوادگی، موبایل یا نام کاربری..."
                    />

                    {selectedUser && (
                      <button
                        type="button"
                        style={clearUserButtonStyle}
                        onClick={clearSelectedUser}
                        title="حذف انتخاب"
                      >
                        <FaTimes />
                      </button>
                    )}

                    {!selectedUser && (
                      <div style={searchIconStyle}>
                        {usersLoading ? "..." : <FaSearch />}
                      </div>
                    )}
                  </div>

                  {isUserDropdownOpen && (
                    <div style={userDropdownStyle}>
                      <div style={dropdownHeaderStyle}>
                        <span>
                          {usersLoading
                            ? "در حال دریافت کاربران..."
                            : `پیشنهاد کاربران (${users.length})`}
                        </span>

                        <button
                          type="button"
                          style={dropdownCloseButtonStyle}
                          onClick={() => setIsUserDropdownOpen(false)}
                          title="بستن"
                        >
                          <FaTimes />
                        </button>
                      </div>

                      <div style={dropdownListStyle}>
                        {users.length === 0 && !usersLoading ? (
                          <div style={emptyUsersStyle}>
                            کاربری برای نمایش وجود ندارد.
                          </div>
                        ) : (
                          users.map((user) => (
                            <div
                              key={user.id}
                              onClick={() => handleSelectUser(user)}
                              style={{
                                ...userItemStyle,
                                ...(selectedUser?.id === user.id
                                  ? selectedUserItemStyle
                                  : {}),
                              }}
                            >
                              <div>
                                <strong>
                                  {user.name || ""} {user.family || ""}
                                </strong>

                                <div
                                  style={{
                                    color: "#95a5a6",
                                    fontSize: "12px",
                                    marginTop: "4px",
                                  }}
                                >
                                  {user.username} | {user.phone_num1}
                                </div>
                              </div>

                              <div style={deviceCountBadgeStyle}>
                                {user.devices_count || 0} دستگاه
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {selectedUser && (
                  <div style={selectedUserBoxStyle}>
                    کاربر انتخاب‌شده:
                    <strong>
                      {" "}
                      {selectedUser.name || ""} {selectedUser.family || ""}
                    </strong>
                    <span style={{ color: "#bdc3c7", marginRight: "8px" }}>
                      ({selectedUser.phone_num1})
                    </span>
                  </div>
                )}
              </>
            )}

            <div style={sectionTitleStyle}>
              <FaMicrochip color="#f1c40f" />
              <span>
                {mode === "new_user"
                  ? "اطلاعات دستگاه اول"
                  : "اطلاعات دستگاه جدید"}
              </span>
            </div>

            <div style={getGridStyle(isMobile)}>
              <Input
                label="سریال دستگاه *"
                value={form.device_serial}
                onChange={(v) => {
                  updateField("device_serial", v);
                  resetResultAndError();
                }}
                placeholder="مثلا iot10bae3c0e8fc"
              />

              <Input
                label="نام دستگاه"
                value={form.device_name}
                onChange={(v) => {
                  updateField("device_name", v);
                  resetResultAndError();
                }}
                placeholder="مثلا گلخانه شماره ۱"
              />

              <Input
                label="شماره سرویس *"
                value={form.service_number}
                onChange={(v) => {
                  updateField("service_number", v);
                  resetResultAndError();
                }}
                placeholder="مثلا SRV-1405-0001"
              />

              <JalaliDateInput
                label="تاریخ شروع سرویس"
                value={form.start_date}
                isMobile={isMobile}
                onChange={(v) => {
                  updateField("start_date", v);
                  resetResultAndError();
                }}
              />

              <JalaliDateInput
                label="تاریخ پایان سرویس"
                value={form.due_date}
                isMobile={isMobile}
                onChange={(v) => {
                  updateField("due_date", v);
                  resetResultAndError();
                }}
              />
            </div>

            {error && <div style={errorStyle}>{error}</div>}

            <button type="submit" disabled={loading} style={submitButtonStyle}>
              <FaSave />
              {loading
                ? "در حال ثبت..."
                : mode === "new_user"
                  ? "ثبت کاربر و دستگاه"
                  : "افزودن دستگاه به کاربر"}
            </button>
          </form>

          <div style={getSideCardStyle(isMobile)}>
            <div style={sectionTitleStyle}>
              <FaCheckCircle color="#3498db" />
              <span>خروجی عملیات</span>
            </div>

            {!result ? (
              <div style={emptyResultStyle}>
                {mode === "new_user"
                  ? "پس از ثبت موفق، اطلاعات ورود کاربر و دستگاه در این بخش نمایش داده می‌شود."
                  : "پس از افزودن دستگاه، اطلاعات دستگاه جدید در این بخش نمایش داده می‌شود."}
              </div>
            ) : (
              <>
                <div style={successBoxStyle}>
                  <div
                    style={{
                      color: "#2ecc71",
                      fontWeight: "bold",
                      marginBottom: "10px",
                    }}
                  >
                    {result.message || "عملیات با موفقیت انجام شد"}
                  </div>

                  <InfoRow label="نام کاربری" value={result.user.username} />

                  {result.mode === "new_user" && (
                    <InfoRow
                      label="رمز اولیه"
                      value={result.user.plain_password}
                    />
                  )}

                  <InfoRow
                    label="نام کاربر"
                    value={`${result.user.name || ""} ${result.user.family || ""}`}
                  />
                  <InfoRow
                    label="شماره موبایل"
                    value={result.user.phone_num1}
                  />
                  <InfoRow
                    label="سریال دستگاه"
                    value={result.device.device_serial}
                  />
                  <InfoRow
                    label="شماره سرویس"
                    value={result.device.service_number || "-"}
                  />
                  <InfoRow
                    label="نام دستگاه"
                    value={result.device.device_name || "-"}
                  />
                </div>

                <button
                  type="button"
                  style={copyButtonStyle}
                  onClick={() => copyText(buildUserMessage())}
                >
                  <FaCopy />
                  کپی متن ارسال به کاربر
                </button>

                <button
                  type="button"
                  style={newButtonStyle}
                  onClick={handleNewForm}
                >
                  ثبت مورد جدید
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const jalaliMonthNames = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];
const jalaliWeekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

const toPersianDigits = (value) =>
  String(value ?? "").replace(/\d/g, (d) => persianDigits[Number(d)]);
const pad2 = (value) => String(value).padStart(2, "0");
const div = (a, b) => Math.floor(a / b);

const gregorianToJalaliParts = (gy, gm, gd) => {
  const gDaysInMonth = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
  let jy;
  if (gy > 1600) {
    jy = 979;
    gy -= 1600;
  } else {
    jy = 0;
    gy -= 621;
  }
  const gy2 = gm > 2 ? gy + 1 : gy;
  let days =
    365 * gy +
    div(gy2 + 3, 4) -
    div(gy2 + 99, 100) +
    div(gy2 + 399, 400) -
    80 +
    gd +
    gDaysInMonth[gm - 1];
  jy += 33 * div(days, 12053);
  days %= 12053;
  jy += 4 * div(days, 1461);
  days %= 1461;
  if (days > 365) {
    jy += div(days - 1, 365);
    days = (days - 1) % 365;
  }
  const jm = days < 186 ? 1 + div(days, 31) : 7 + div(days - 186, 30);
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
  return { jy, jm, jd };
};

const jalaliToGregorianParts = (jy, jm, jd) => {
  let gy;
  if (jy > 979) {
    gy = 1600;
    jy -= 979;
  } else {
    gy = 621;
  }
  let days =
    365 * jy +
    div(jy, 33) * 8 +
    div((jy % 33) + 3, 4) +
    78 +
    jd +
    (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
  gy += 400 * div(days, 146097);
  days %= 146097;
  if (days > 36524) {
    gy += 100 * div(--days, 36524);
    days %= 36524;
    if (days >= 365) days += 1;
  }
  gy += 4 * div(days, 1461);
  days %= 1461;
  if (days > 365) {
    gy += div(days - 1, 365);
    days = (days - 1) % 365;
  }
  let gd = days + 1;
  const leap = (gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0;
  const gMonthDays = [
    0,
    31,
    leap ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  let gm = 1;
  while (gm <= 12 && gd > gMonthDays[gm]) {
    gd -= gMonthDays[gm];
    gm += 1;
  }
  return { gy, gm, gd };
};

const parseGregorianDate = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [gy, gm, gd] = value.split("-").map(Number);
  const date = new Date(gy, gm - 1, gd);
  if (
    date.getFullYear() !== gy ||
    date.getMonth() !== gm - 1 ||
    date.getDate() !== gd
  )
    return null;
  return { gy, gm, gd };
};

const formatJalaliDate = (gregorianDate) => {
  const parsed = parseGregorianDate(gregorianDate);
  if (!parsed) return "";
  const { jy, jm, jd } = gregorianToJalaliParts(
    parsed.gy,
    parsed.gm,
    parsed.gd,
  );
  return toPersianDigits(`${jy}/${pad2(jm)}/${pad2(jd)}`);
};

const toGregorianDateString = (jy, jm, jd) => {
  const { gy, gm, gd } = jalaliToGregorianParts(jy, jm, jd);
  return `${gy}-${pad2(gm)}-${pad2(gd)}`;
};

const getTodayGregorianDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
};

const getJalaliMonthLength = (jy, jm) => {
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  const thisYearStart = jalaliToGregorianParts(jy, 1, 1);
  const nextYearStart = jalaliToGregorianParts(jy + 1, 1, 1);
  const start = new Date(
    thisYearStart.gy,
    thisYearStart.gm - 1,
    thisYearStart.gd,
  );
  const next = new Date(
    nextYearStart.gy,
    nextYearStart.gm - 1,
    nextYearStart.gd,
  );
  return Math.round((next - start) / 86400000) === 366 ? 30 : 29;
};

const getJalaliFirstWeekday = (jy, jm) => {
  const { gy, gm, gd } = jalaliToGregorianParts(jy, jm, 1);
  return (new Date(gy, gm - 1, gd).getDay() + 1) % 7;
};

const useIsMobile = (breakpoint = 720) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false,
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);
  return isMobile;
};

const JalaliDateInput = ({
  label,
  value,
  onChange,
  placeholder = "انتخاب تاریخ",
  isMobile = false,
  inputStyleOverride = inputStyle,
  labelStyleOverride = labelStyle,
}) => {
  const wrapperRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const selectedParts = parseGregorianDate(value);
  const selectedJalali = selectedParts
    ? gregorianToJalaliParts(
        selectedParts.gy,
        selectedParts.gm,
        selectedParts.gd,
      )
    : null;
  const todayParts = parseGregorianDate(getTodayGregorianDate());
  const todayJalali = todayParts
    ? gregorianToJalaliParts(todayParts.gy, todayParts.gm, todayParts.gd)
    : null;
  const [view, setView] = useState(
    () => selectedJalali || todayJalali || { jy: 1405, jm: 1, jd: 1 },
  );

  useEffect(() => {
    if (selectedJalali)
      setView({ jy: selectedJalali.jy, jm: selectedJalali.jm, jd: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const moveMonth = (offset) => {
    setView((prev) => {
      let nextYear = prev.jy;
      let nextMonth = prev.jm + offset;
      while (nextMonth < 1) {
        nextMonth += 12;
        nextYear -= 1;
      }
      while (nextMonth > 12) {
        nextMonth -= 12;
        nextYear += 1;
      }
      return { jy: nextYear, jm: nextMonth, jd: 1 };
    });
  };

  const selectDay = (day) => {
    onChange(toGregorianDateString(view.jy, view.jm, day));
    setIsOpen(false);
  };

  const days = [];
  for (let i = 0; i < getJalaliFirstWeekday(view.jy, view.jm); i += 1)
    days.push(null);
  for (let day = 1; day <= getJalaliMonthLength(view.jy, view.jm); day += 1)
    days.push(day);

  return (
    <div style={dateInputWrapperStyle} ref={wrapperRef}>
      <label style={labelStyleOverride}>{label}</label>
      <button
        type="button"
        style={{
          ...inputStyleOverride,
          ...dateButtonStyle,
          borderColor: value
            ? "rgba(46,204,113,0.42)"
            : inputStyleOverride.border,
        }}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>{value ? formatJalaliDate(value) : placeholder}</span>
        <FaCalendarAlt style={{ color: "#f1c40f", flex: "0 0 auto" }} />
      </button>
      {value && <div style={gregorianDateHintStyle}>ذخیره میلادی: {value}</div>}
      {isOpen && (
        <div style={getDatePickerStyle(isMobile)}>
          <div style={datePickerHeaderStyle}>
            <button
              type="button"
              style={dateNavButtonStyle}
              onClick={() => moveMonth(-1)}
            >
              ‹
            </button>
            <strong>
              {jalaliMonthNames[view.jm - 1]} {toPersianDigits(view.jy)}
            </strong>
            <button
              type="button"
              style={dateNavButtonStyle}
              onClick={() => moveMonth(1)}
            >
              ›
            </button>
          </div>
          <div style={dateWeekGridStyle}>
            {jalaliWeekDays.map((day) => (
              <span key={day} style={dateWeekDayStyle}>
                {day}
              </span>
            ))}
          </div>
          <div style={dateDaysGridStyle}>
            {days.map((day, idx) => {
              const selected = Boolean(
                selectedJalali &&
                selectedJalali.jy === view.jy &&
                selectedJalali.jm === view.jm &&
                selectedJalali.jd === day,
              );
              const isToday = Boolean(
                todayJalali &&
                todayJalali.jy === view.jy &&
                todayJalali.jm === view.jm &&
                todayJalali.jd === day,
              );
              return day ? (
                <button
                  key={`${view.jy}-${view.jm}-${day}`}
                  type="button"
                  style={{
                    ...dateDayButtonStyle,
                    ...(selected ? selectedDateDayStyle : {}),
                    ...(isToday && !selected ? todayDateDayStyle : {}),
                  }}
                  onClick={() => selectDay(day)}
                >
                  {toPersianDigits(day)}
                </button>
              ) : (
                <span key={`empty-${idx}`} />
              );
            })}
          </div>
          <div style={datePickerFooterStyle}>
            <button
              type="button"
              style={dateFooterButtonStyle}
              onClick={() => {
                onChange(getTodayGregorianDate());
                setIsOpen(false);
              }}
            >
              امروز
            </button>
            <button
              type="button"
              style={{
                ...dateFooterButtonStyle,
                background: "rgba(231,76,60,0.16)",
                color: "#ffb3b3",
              }}
              onClick={() => {
                onChange("");
                setIsOpen(false);
              }}
            >
              پاک کردن
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Input = ({ label, value, onChange, type = "text", placeholder = "" }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={inputStyle}
    />
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={infoRowStyle}>
    <span style={{ color: "#95a5a6" }}>{label}:</span>
    <strong>{value || "-"}</strong>
  </div>
);

const modeSwitchStyle = {
  display: "flex",
  gap: "12px",
  marginTop: "24px",
  direction: "rtl",
  flexWrap: "wrap",
};

const modeButtonStyle = {
  border: "1px solid rgba(255,255,255,0.1)",
  background: "rgba(31,45,58,0.85)",
  color: "#bdc3c7",
  borderRadius: "12px",
  padding: "12px 18px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: "bold",
};

const activeModeButtonStyle = {
  background: "rgba(46,204,113,0.18)",
  color: "#2ecc71",
  border: "1px solid rgba(46,204,113,0.45)",
};

const pageGridStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 2fr) minmax(320px, 1fr)",
  gap: "20px",
  width: "100%",
  marginTop: "20px",
  direction: "rtl",
};

const formCardStyle = {
  background: "rgba(31, 45, 58, 0.92)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "16px",
  padding: "20px",
  color: "#fff",
  boxShadow: "0 10px 35px rgba(0,0,0,0.25)",
};

const sideCardStyle = {
  ...formCardStyle,
  height: "fit-content",
};

const sectionTitleStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: "#f1c40f",
  fontWeight: "bold",
  marginBottom: "16px",
  marginTop: "4px",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(220px, 1fr))",
  gap: "14px",
  marginBottom: "22px",
};

const labelStyle = {
  display: "block",
  marginBottom: "6px",
  fontSize: "13px",
  color: "#bdc3c7",
};

const inputStyle = {
  width: "100%",
  background: "#101820",
  color: "#fff",
  border: "1px solid #34495e",
  borderRadius: "8px",
  padding: "10px",
  outline: "none",
  boxSizing: "border-box",
};

const smallButtonStyle = {
  background: "#3498db",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "0 12px",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const userDropdownWrapperStyle = {
  position: "relative",
  marginBottom: "18px",
};

const userSearchInputWrapperStyle = {
  position: "relative",
};

const clearUserButtonStyle = {
  position: "absolute",
  left: "8px",
  top: "50%",
  transform: "translateY(-50%)",
  width: "26px",
  height: "26px",
  borderRadius: "50%",
  border: "none",
  background: "rgba(231, 76, 60, 0.18)",
  color: "#ffb3b3",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const searchIconStyle = {
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#95a5a6",
  fontSize: "13px",
};

const userDropdownStyle = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  left: 0,
  zIndex: 50,
  background: "#101820",
  border: "1px solid rgba(52, 152, 219, 0.35)",
  borderRadius: "12px",
  boxShadow: "0 14px 40px rgba(0,0,0,0.45)",
  overflow: "hidden",
};

const dropdownHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "9px 11px",
  background: "#17212b",
  color: "#bdc3c7",
  fontSize: "12px",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

const dropdownCloseButtonStyle = {
  width: "24px",
  height: "24px",
  border: "none",
  borderRadius: "50%",
  background: "rgba(255,255,255,0.06)",
  color: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const dropdownListStyle = {
  maxHeight: "260px",
  overflowY: "auto",
  padding: "8px",
  display: "grid",
  gap: "8px",
};

const userItemStyle = {
  background: "#17212b",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "10px",
  padding: "11px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
};

const selectedUserItemStyle = {
  border: "1px solid rgba(46,204,113,0.55)",
  background: "rgba(46,204,113,0.12)",
};

const deviceCountBadgeStyle = {
  background: "rgba(52,152,219,0.14)",
  color: "#4dabf7",
  border: "1px solid rgba(52,152,219,0.28)",
  borderRadius: "999px",
  padding: "4px 9px",
  fontSize: "11px",
  whiteSpace: "nowrap",
};

const selectedUserBoxStyle = {
  background: "rgba(46,204,113,0.12)",
  border: "1px solid rgba(46,204,113,0.32)",
  color: "#ecf0f1",
  borderRadius: "10px",
  padding: "10px",
  marginBottom: "18px",
  fontSize: "13px",
};

const emptyUsersStyle = {
  background: "#17212b",
  borderRadius: "10px",
  padding: "16px",
  color: "#95a5a6",
  fontSize: "13px",
  border: "1px dashed rgba(255,255,255,0.12)",
};

const submitButtonStyle = {
  width: "100%",
  background: "#2ecc71",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  padding: "13px",
  fontWeight: "bold",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
};

const copyButtonStyle = {
  width: "100%",
  marginTop: "12px",
  background: "#3498db",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "11px 14px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
};

const newButtonStyle = {
  ...copyButtonStyle,
  background: "#34495e",
};

const errorStyle = {
  background: "rgba(231, 76, 60, 0.15)",
  border: "1px solid rgba(231, 76, 60, 0.4)",
  color: "#ffb3b3",
  borderRadius: "8px",
  padding: "10px",
  marginBottom: "12px",
  fontSize: "13px",
};

const emptyResultStyle = {
  background: "#101820",
  borderRadius: "10px",
  padding: "20px",
  color: "#95a5a6",
  lineHeight: "2",
  fontSize: "13px",
  border: "1px dashed rgba(255,255,255,0.12)",
};

const successBoxStyle = {
  background: "#101820",
  borderRadius: "10px",
  padding: "14px",
  color: "#ecf0f1",
  border: "1px solid rgba(46, 204, 113, 0.25)",
};

const infoRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
  padding: "8px 0",
  fontSize: "13px",
};

const dateInputWrapperStyle = { position: "relative" };
const dateButtonStyle = {
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  textAlign: "right",
  minHeight: "42px",
};
const gregorianDateHintStyle = {
  marginTop: "5px",
  color: "#7f8c8d",
  fontSize: "11px",
  direction: "ltr",
  textAlign: "left",
};
const getDatePickerStyle = (isMobile) => ({
  position: isMobile ? "fixed" : "absolute",
  top: isMobile ? "auto" : "calc(100% + 8px)",
  right: isMobile ? "14px" : 0,
  left: isMobile ? "14px" : "auto",
  bottom: isMobile ? "14px" : "auto",
  width: isMobile ? "auto" : "320px",
  maxHeight: isMobile ? "78vh" : "none",
  overflowY: "auto",
  background: "#101820",
  color: "#fff",
  border: "1px solid rgba(52,152,219,0.42)",
  borderRadius: "16px",
  padding: "12px",
  boxShadow: "0 18px 50px rgba(0,0,0,0.55)",
  zIndex: 100000,
  direction: "rtl",
});
const datePickerHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "10px",
  color: "#f1c40f",
};
const dateNavButtonStyle = {
  width: "34px",
  height: "34px",
  borderRadius: "10px",
  border: "1px solid rgba(255,255,255,0.1)",
  background: "#17212b",
  color: "#fff",
  cursor: "pointer",
  fontSize: "22px",
  lineHeight: 1,
};
const dateWeekGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  gap: "5px",
  marginBottom: "6px",
};
const dateWeekDayStyle = {
  textAlign: "center",
  color: "#95a5a6",
  fontSize: "12px",
};
const dateDaysGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  gap: "5px",
};
const dateDayButtonStyle = {
  height: "36px",
  borderRadius: "10px",
  border: "1px solid rgba(255,255,255,0.08)",
  background: "#17212b",
  color: "#ecf0f1",
  cursor: "pointer",
  fontWeight: "bold",
};
const selectedDateDayStyle = {
  background: "rgba(46,204,113,0.28)",
  border: "1px solid rgba(46,204,113,0.65)",
  color: "#2ecc71",
};
const todayDateDayStyle = {
  border: "1px solid rgba(241,196,15,0.55)",
  color: "#f1c40f",
};
const datePickerFooterStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "8px",
  marginTop: "10px",
};
const dateFooterButtonStyle = {
  border: "none",
  borderRadius: "10px",
  padding: "9px 10px",
  background: "rgba(52,152,219,0.18)",
  color: "#4dabf7",
  cursor: "pointer",
  fontWeight: "bold",
};

const getModeSwitchStyle = (isMobile) => ({
  ...modeSwitchStyle,
  ...(isMobile ? { flexDirection: "column" } : {}),
});
const getModeButtonStyle = (isMobile, isActive) => ({
  ...modeButtonStyle,
  ...(isMobile ? { width: "100%", justifyContent: "center" } : {}),
  ...(isActive ? activeModeButtonStyle : {}),
});
const getPageGridStyle = (isMobile) => ({
  ...pageGridStyle,
  ...(isMobile ? { gridTemplateColumns: "1fr", gap: "14px" } : {}),
});
const getFormCardStyle = (isMobile) => ({
  ...formCardStyle,
  ...(isMobile ? { padding: "14px", borderRadius: "14px" } : {}),
});
const getSideCardStyle = (isMobile) => ({
  ...sideCardStyle,
  ...(isMobile ? { padding: "14px", borderRadius: "14px" } : {}),
});
const getGridStyle = (isMobile) => ({
  ...gridStyle,
  ...(isMobile ? { gridTemplateColumns: "1fr", gap: "12px" } : {}),
});

export default AdminCreateUserDevice;
